import { useLocalSearchParams, useRouter } from "expo-router";
import { useEffect, useState } from "react";
import { VStack } from "@/components/ui/vstack";
import { Spinner } from "@/components/ui/spinner";
import { Text } from "@/components/ui/text";
import { Button, ButtonText, ButtonSpinner, ButtonIcon } from "@/components/ui/button";
import {
  useJoinByCodeControllerFindOrganization,
  useJoinByCodeControllerJoinByCode,
} from "@/generated/api/api";
import {
  getOrganizationControllerListQueryKey,
  getUserPartControllerMyParticipantsQueryKey,
} from "@/generated/api/api";
import { useQueryClient } from "@tanstack/react-query";
import { Check } from "lucide-react-native";
import { Link as ExpoLink } from "expo-router";

export default function JoinByCodePage() {
  const { code } = useLocalSearchParams<{ code: string }>();
  const router = useRouter();
  const queryClient = useQueryClient();
  const normalizedCode = (code || "").toUpperCase();

  const { data: organization, isLoading, error } =
    useJoinByCodeControllerFindOrganization(normalizedCode, {
      query: { enabled: normalizedCode.length === 6, retry: false },
    });

  const { mutate: joinByCode, isPending: isJoining } = useJoinByCodeControllerJoinByCode();

  const onJoin = () => {
    joinByCode(
      { code: normalizedCode },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getUserPartControllerMyParticipantsQueryKey(),
          });
          queryClient.invalidateQueries({
            queryKey: getOrganizationControllerListQueryKey(),
          });
          router.replace("/(tabs)/orgs");
        },
      }
    );
  };

  if (isLoading) {
    return (
      <VStack className="gap-6 items-center">
        <Spinner size="large" />
        <Text>Validating invitation code...</Text>
      </VStack>
    );
  }

  if (error || !organization) {
    return (
      <VStack className="gap-6 items-center">
        <Text className="text-error-500 text-center">
          Invalid invitation code. Please check and try again.
        </Text>
        <Button onPress={() => router.replace("/")}>
          <ButtonText>Go to home</ButtonText>
        </Button>
      </VStack>
    );
  }

  return (
    <VStack className="gap-6 items-center">
      <VStack className="gap-2 bg-background-50 p-6 rounded-lg w-full items-center">
        <Text size="sm" className="text-typography-500">
          You're joining:
        </Text>
        <Text bold size="2xl" className="text-center">
          {organization.name}
        </Text>
        {organization.address && (
          <Text size="sm" className="text-typography-500 text-center">
            {organization.address}
          </Text>
        )}
      </VStack>
      <Text size="sm" className="text-center">
        By clicking "Join" you agree to participate in their tips-sharing scheme.
      </Text>
      <Text size="sm" className="text-center">
        Learn more in our{" "}
        <ExpoLink href="https://gotips.co/client-terms/" target="_blank">
          <Text size="sm" bold>
            Terms of Use
          </Text>
        </ExpoLink>
      </Text>
      <Button onPress={onJoin} isDisabled={isJoining} size="lg" className="w-full">
        <ButtonIcon as={Check} />
        <ButtonText>Join team</ButtonText>
        {isJoining && <ButtonSpinner />}
      </Button>
    </VStack>
  );
}
